import MarkdownIt from 'markdown-it';
import { useLocalStore, useObserver } from 'mobx-react-lite';
import { getEnv } from 'mobx-state-tree';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components/macro';
import { countryCodes } from '../constants/countryCodes';
import fadeIn from '../keyframes/fadeIn';
import { useMst } from '../models/WithWineState';
import { ClientOnlyAPI } from '../types/AvailableAPI';
import { Env } from '../types/Env';
import { JoinMailingListProps } from '../types/JoinMailingListProps';
import { abortableFetch } from '../utils/abortableFetch';
import { isValidEmail } from '../utils/isValidEmail';
import { parseBoolean } from '../utils/parseBoolean';
import Button from './Button';
import Tickbox from './Tickbox';
import { JoinMailingEventData } from '../types/EventDataProps';
import { dispatchJoinMailingListData } from '../utils/dispatchHelper';

const countries = Array.from(countryCodes.entries());

interface CountryState {
  id: number;
  code: string;
  name: string;
}

interface CountryStateAPIItem {
  id: number;
  country: string;
  countryCode: string;
  description: string;
  code: string;
}

const UnstyledJoinMailingList: React.FC<JoinMailingListProps> = ({
  className,
  phone,
  postcode,
  country,
  state,
  mailinglistname,
  mailinglistbutton,
  mailinglistconfirmation,
  termsandconditions,
  conversiontag,
}) => {
  const name = mailinglistname || 'Mailing List';
  const defaultCountry = useMemo(() => {
    if (country?.defaultvalue) {
      // country code
      return (
        countries.find((entry) => entry[1] === country.defaultvalue)?.[0] ?? ''
      );
    }
    return '';
  }, [country]);

  const phoneRegexPattern = useMemo(() => {
    if (typeof phone?.pattern === 'string') {
      return new RegExp(phone.pattern);
    }
    return new RegExp('^[\\s0-9\\+]*$');
  }, [phone]);

  const tnc = useMemo(() => {
    const md = new MarkdownIt();
    if (termsandconditions?.content) {
      return md.render(termsandconditions.content);
    }
    return undefined;
  }, [termsandconditions]);

  const phone_enabled = parseBoolean(phone?.enabled, false);
  const country_enabled = parseBoolean(country?.enabled, false);
  const state_enabled = parseBoolean(state?.enabled, false);
  const postcode_enabled = parseBoolean(postcode?.enabled, false);
  const phone_required = parseBoolean(phone?.required, false);
  const country_required = parseBoolean(country?.required, false);
  const state_required = parseBoolean(state?.required, false);
  const postcode_required = parseBoolean(postcode?.required, false);
  const country_search = parseBoolean(country?.search, false);
  const state_search = parseBoolean(state?.search, false);
  const tnc_required = parseBoolean(termsandconditions?.required, false);

  const stateTree = useMst();
  const localState = useLocalStore(() => ({
    membership: 'not-ready',
    formDirty: false,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: defaultCountry,
    countryCode: defaultCountry ? countryCodes.get(defaultCountry) : '',
    state: '',
    stateCode: '',
    postcode: '',
    validEmail: false,
    validPhone: phone_enabled ? !phone_required : true,
    validCountry: country_enabled ? !country_required : true,
    validState:
      (country_enabled ? !country_required : true) &&
      (state_enabled ? !state_required : true),
    validPostcode: postcode_enabled ? !postcode_required : true,
    validTnc: termsandconditions ? !tnc_required : true,
    loading: false,
    error: '',
    states: [],
    acceptedTnc: false,

    get isMailingListMember() {
      return stateTree.session?.mailingListStatus;
    },

    setMembershipState(
      membership: 'not-ready' | 'member' | 'non-member' | 'auth-non-member',
    ) {
      localState.membership = membership;
    },

    setFirstName(firstName: string) {
      localState.firstName = firstName;
    },

    setLastName(lastName: string) {
      localState.lastName = lastName;
    },

    setEmail(email: string) {
      localState.email = email;
      localState.validEmail = isValidEmail(email);
    },

    setPhone(value: string) {
      localState.phone = value;
      localState.validPhone = !(!value && phone_enabled && phone_required);
      if (phoneRegexPattern) {
        localState.validPhone =
          localState.validPhone && phoneRegexPattern.test(value);
      }
    },

    setCountry(value: string) {
      const countryCode = countryCodes.get(value) ?? '';
      localState.country = value;
      localState.countryCode = countryCode;
      localState.validCountry = !(
        !localState.countryCode &&
        country_enabled &&
        country_required
      );
      localState.fetchStates(countryCode);
    },

    setCountryState(value: string) {
      const stateCode =
        (localState.states as CountryState[]).find((s) => s.name === value)
          ?.code ?? '';
      localState.state = value;
      localState.stateCode = localState.states.length > 0 ? stateCode : value;
      localState.validState =
        (value && localState.states.length === 0) ||
        (localState.validCountry &&
          !(!localState.stateCode && state_enabled && state_required));
    },

    setPostCode(value: string) {
      localState.postcode = value;
      localState.validPostcode = !(
        !value &&
        postcode_enabled &&
        postcode_required
      );
    },

    toggleTnc() {
      localState.acceptedTnc = !localState.acceptedTnc;
      localState.validTnc = !tnc_required || localState.acceptedTnc;
    },

    resetForm() {
      localState.firstName = '';
      localState.lastName = '';
      localState.email = '';
      localState.loading = false;
      localState.formDirty = false;
      localState.error = '';
      localState.validEmail = false;
      localState.validPhone = phone_enabled ? !phone_required : true;
      localState.validCountry = country_enabled ? !country_required : true;
      localState.validState =
        localState.validCountry && (state_enabled ? !state_required : true);
      localState.validPostcode = postcode_enabled ? !postcode_required : true;
      localState.validTnc = termsandconditions ? !tnc_required : true;
      localState.phone = '';
      localState.postcode = '';
      localState.country = defaultCountry;
      localState.countryCode = defaultCountry
        ? countryCodes.get(defaultCountry)
        : '';
      localState.acceptedTnc = false;
    },

    fetchMembershipState() {
      stateTree.session?.loadIsMailingListMember().then(() => {
        if (localState.isMailingListMember === null) {
          localState.setMembershipState('not-ready');
          return;
        }

        if (!stateTree.session?.authenticated) {
          localState.setMembershipState('non-member');
          return;
        }

        localState.setMembershipState(
          localState.isMailingListMember ? 'member' : 'auth-non-member',
        );
      });
    },

    onJoinButtonClick(e: React.FormEvent) {
      e.preventDefault();
      localState.error = '';

      const dispatchDetail: JoinMailingEventData = {
        v1: {
          email: localState.email,
          firstName: localState.firstName,
          lastName: localState.lastName,
          phone: localState.phone,
          countryCode: localState.countryCode,
          stateCode: localState.stateCode,
          postcode: localState.postcode,
          acceptTermsAndConditions: localState.acceptedTnc,
        },
      };

      dispatchJoinMailingListData(dispatchDetail);

      if (
        (!localState.validEmail ||
          !localState.validPhone ||
          !localState.validPostcode ||
          !localState.validCountry ||
          !localState.validState ||
          !localState.validTnc) &&
        localState.membership === 'non-member'
      ) {
        localState.formDirty = true;
        return;
      }

      localState.loading = true;

      (localState.membership === 'auth-non-member'
        ? stateTree.session?.followBrand(localState.acceptedTnc, conversiontag)
        : stateTree.session?.followBrandByEmail({
            email: localState.email,
            firstName: localState.firstName,
            lastName: localState.lastName,
            phone: localState.phone,
            countryCode: localState.countryCode,
            stateCode: localState.stateCode,
            postcode: localState.postcode,
            acceptTermsAndConditions: localState.acceptedTnc,
            tag: conversiontag,
          })
      )
        ?.then(() => {
          localState.setMembershipState('member');
        })
        .catch((error) => {
          localState.resetForm();
          localState.loading = false;
          localState.error = error;
        });
    },

    fetchStates(countryCode: string) {
      if (!countryCode) {
        return;
      }

      const env = getEnv<Env>(stateTree);
      localState.loading = true;
      localState.setCountryState('');
      abortableFetch(
        `${env.apiUrl}${ClientOnlyAPI.states}?country=${countryCode}`,
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
          },
        },
      )
        .then((response) => response.json())
        .then((result: CountryStateAPIItem[]) => {
          const states = result.reduce<CountryState[]>((acc, curr) => {
            if (curr.description ?? curr.code) {
              acc.push({
                id: curr.id,
                name: curr.description,
                code: curr.description ?? curr.code,
              });
            }
            return acc;
          }, []) as any;

          localState.states = states;
        })
        .finally(() => {
          localState.loading = false;
        });
    },
  }));

  useEffect(() => {
    localState.fetchMembershipState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (defaultCountry) {
      localState.setCountry(defaultCountry);
    }
  }, [localState, defaultCountry]);

  return useObserver(() => (
    <div className={'ww-widget ww-join-mailing-list ' + (className ?? '')}>
      {localState.membership === 'member' ? (
        <>
          <p className="ww-already-member">
            {mailinglistconfirmation
              ? mailinglistconfirmation
              : `You have joined the ${name}`}
          </p>
        </>
      ) : (
        <>
          {localState.error && (
            <p className="ww-general-error">
              Sorry, joining the {name.toLowerCase()} is unsuccessful, please
              try again.
            </p>
          )}
          <form onSubmit={localState.onJoinButtonClick}>
            {localState.membership === 'non-member' && (
              <>
                <label className="ww-field">
                  <span className="ww-field-label">First Name</span>
                  <input
                    type="text"
                    className="ww-text-input"
                    value={localState.firstName}
                    disabled={localState.loading}
                    onChange={(e) => localState.setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                  />
                </label>
                <label className="ww-field">
                  <span className="ww-field-label">Last Name</span>
                  <input
                    type="text"
                    className="ww-text-input"
                    value={localState.lastName}
                    disabled={localState.loading}
                    onChange={(e) => localState.setLastName(e.target.value)}
                    placeholder="Enter your last name"
                  />
                </label>
                <label className="ww-field">
                  <span className="ww-field-label">Email *</span>
                  <input
                    type="text"
                    className="ww-text-input"
                    value={localState.email}
                    disabled={localState.loading}
                    onChange={(e) => localState.setEmail(e.target.value)}
                    placeholder="Enter a valid email"
                  />
                  {!localState.validEmail && localState.formDirty && (
                    <small className="ww-field-error">
                      Your email is not valid.
                    </small>
                  )}
                </label>

                {phone_enabled && (
                  <label className="ww-field">
                    <span className="ww-field-label">
                      Phone Number
                      {phone_required ? (
                        <span className="ww-field-required">&nbsp;*</span>
                      ) : (
                        ''
                      )}
                    </span>
                    <input
                      type="text"
                      className="ww-text-input"
                      value={localState.phone}
                      disabled={localState.loading}
                      onChange={(e) => localState.setPhone(e.target.value)}
                      placeholder="Enter your phone number"
                    />
                    {!localState.validPhone && localState.formDirty && (
                      <small className="ww-field-error">
                        {!phoneRegexPattern
                          ? 'Phone Number is required.'
                          : 'Enter a valid phone number'}
                      </small>
                    )}
                  </label>
                )}

                {country_enabled && (
                  <label className="ww-field">
                    <span className="ww-field-label">
                      Country
                      {country_required ? (
                        <span className="ww-field-required">&nbsp;*</span>
                      ) : (
                        ''
                      )}
                    </span>

                    {country_search ? (
                      <>
                        <input
                          type="text"
                          className="ww-text-input"
                          value={localState.country}
                          disabled={localState.loading}
                          onChange={(e) =>
                            localState.setCountry(e.target.value)
                          }
                          list="countries"
                          placeholder="Enter your country"
                        />
                        <datalist id="countries">
                          {countries.map((c) => (
                            <option key={c[1]} value={c[0]}>
                              {c[0]}
                            </option>
                          ))}
                        </datalist>
                      </>
                    ) : (
                      <>
                        <select
                          className="ww-text-input"
                          value={localState.country}
                          disabled={localState.loading}
                          onChange={(e) =>
                            localState.setCountry(e.target.value)
                          }
                        >
                          <option key={'country_novalue'} value={''}>
                            Enter your country
                          </option>
                          {countries.map((c) => (
                            <option key={c[1]}>{c[0]}</option>
                          ))}
                        </select>
                      </>
                    )}

                    {!localState.validCountry && localState.formDirty && (
                      <small className="ww-field-error">
                        Country is required.
                      </small>
                    )}
                  </label>
                )}

                {state_enabled && (
                  <label className="ww-field">
                    <span className="ww-field-label">
                      State
                      {state_required ? (
                        <span className="ww-field-required">&nbsp;*</span>
                      ) : (
                        ''
                      )}
                    </span>

                    {state_search ? (
                      <>
                        <input
                          type="text"
                          className="ww-text-input"
                          value={localState.state}
                          disabled={
                            localState.loading || !localState.validCountry
                          }
                          onChange={(e) =>
                            localState.setCountryState(e.target.value)
                          }
                          list="states"
                          placeholder={
                            localState.loading
                              ? 'Loading...'
                              : !localState.countryCode
                              ? ''
                              : 'Enter your state'
                          }
                        />
                        <datalist id="states">
                          {localState.states.map((s: CountryState) => (
                            <option key={s.id} value={s.code}>
                              {s.code}
                            </option>
                          ))}
                        </datalist>
                      </>
                    ) : (
                      <>
                        <select
                          className="ww-text-input"
                          value={localState.state}
                          disabled={
                            localState.loading || !localState.validCountry
                          }
                          onChange={(e) =>
                            localState.setCountryState(e.target.value)
                          }
                        >
                          <option key={'state_novalue'} value={''}>
                            {localState.loading
                              ? 'Loading...'
                              : !localState.countryCode
                              ? ''
                              : 'Enter your state'}
                          </option>
                          {localState.states.map((s: CountryState) => (
                            <option key={s.id} value={s.code}>
                              {s.code}
                            </option>
                          ))}
                        </select>
                      </>
                    )}

                    {!localState.validState && localState.formDirty && (
                      <small className="ww-field-error">
                        State is required.
                      </small>
                    )}
                  </label>
                )}

                {postcode_enabled && (
                  <label className="ww-field">
                    <span className="ww-field-label">
                      Postcode
                      {postcode_required ? (
                        <span className="ww-field-required">&nbsp;*</span>
                      ) : (
                        ''
                      )}
                    </span>
                    <input
                      type="text"
                      className="ww-text-input"
                      value={localState.postcode}
                      disabled={localState.loading}
                      onChange={(e) => localState.setPostCode(e.target.value)}
                      placeholder="Enter your post code"
                    />
                    {!localState.validPostcode && localState.formDirty && (
                      <small className="ww-field-error">
                        Postcode is required.
                      </small>
                    )}
                  </label>
                )}

                {tnc && (
                  <div className="terms-and-conditions my-5 flex flex-row items-center">
                    <Tickbox
                      className="self-start"
                      name="tnc"
                      active={localState.acceptedTnc}
                      onTick={() => localState.toggleTnc()}
                    />
                    <div className="flex flex-auto flex-col">
                      <span dangerouslySetInnerHTML={{ __html: tnc }} />
                      {!localState.validTnc && localState.formDirty && (
                        <small className="ww-field-error">
                          You must accept the terms and conditions.
                        </small>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            <Button
              primary
              className="join-button"
              type="submit"
              disabled={
                localState.membership === 'not-ready' || localState.loading
              }
            >
              {mailinglistbutton ? mailinglistbutton : `Join ${name}`}
            </Button>
          </form>
        </>
      )}
    </div>
  ));
};

const JoinMailingList = styled(UnstyledJoinMailingList)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 1em;

  .ww-already-member {
    animation: ${fadeIn} 200ms ease-out forwards;
  }

  .ww-field {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 1em;
  }

  .ww-field-label {
    margin-bottom: 0.5em;
    font-weight: bold;
  }

  .ww-text-input {
    padding: 0.5em;
  }

  .ww-field-error {
    margin-top: 0.5em;
    color: var(--warn-color);
  }

  .ww-general-error {
    color: var(--warn-color);
  }
`;

export default JoinMailingList;
